import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fairApi } from '../services/api';
import { CalendarMonthResponse, FairCalendarEvent } from '../types';
import { ChevronLeft, ChevronRight, Calendar, MapPin } from 'lucide-react';

const FairCalendar: React.FC = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState<CalendarMonthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1; // JavaScript months are 0-indexed

  const loadCalendarData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Include test fairs and exclude drafts
      const data = await fairApi.getCalendarMonth(year, month, true, true);
      setCalendarData(data);
    } catch (err: unknown) {
      setError('Failed to load calendar events');
      console.error('Error loading calendar:', err);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  const previousMonth = () => {
    setCurrentDate(new Date(year, month - 2, 1)); // month-2 because JS months are 0-indexed
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month, 1)); // month is already 1-indexed
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number): number => {
    // Returns 0 (Sunday) to 6 (Saturday)
    return new Date(year, month - 1, 1).getDay();
  };

  const getAvailabilityColor = (event: FairCalendarEvent): string => {
    if (event.is_sold_out) {
      return 'bg-gray-200 text-gray-700 border-gray-400'; // Sold out
    }

    const availabilityPercent = event.total_slots > 0
      ? (event.available_slots / event.total_slots) * 100
      : 0;

    if (availabilityPercent > 50) {
      return 'bg-green-100 text-green-800 border-green-400'; // Good availability
    } else if (availabilityPercent > 10) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-400'; // Limited availability
    } else if (availabilityPercent > 0) {
      return 'bg-red-100 text-red-800 border-red-400'; // Very limited
    } else {
      return 'bg-gray-200 text-gray-700 border-gray-400'; // Sold out
    }
  };

  const getAvailabilityBadge = (event: FairCalendarEvent): string => {
    if (event.is_sold_out) {
      return 'Sold Out';
    }
    return `${event.available_slots}/${event.total_slots}`;
  };

  const handleEventClick = (event: FairCalendarEvent) => {
    navigate(`/fair/${event.fair_id}`);
  };

  const renderCalendar = () => {
    if (!calendarData) return null;

    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);

    // Create a map of date -> events
    const eventsByDate = new Map<string, FairCalendarEvent[]>();
    calendarData.days.forEach(day => {
      eventsByDate.set(day.date, day.events);
    });

    // Create calendar grid
    const weeks: JSX.Element[][] = [];
    let currentWeek: JSX.Element[] = [];

    // Add empty cells for days before the first of the month
    for (let i = 0; i < firstDay; i++) {
      currentWeek.push(
        <div key={`empty-${i}`} className="min-h-32 bg-gray-50 border border-gray-200 p-2" />
      );
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const events = eventsByDate.get(dateStr) || [];
      const isToday = dateStr === new Date().toISOString().split('T')[0];

      currentWeek.push(
        <div
          key={day}
          className={`min-h-32 border border-gray-200 p-2 ${isToday ? 'bg-moonbone' : 'bg-white'} hover:bg-gray-50 transition-colors`}
        >
          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-secondary' : 'text-gray-700'}`}>
            {day}
            {isToday && <span className="ml-1 text-xs">(Today)</span>}
          </div>
          <div className="space-y-1 overflow-y-auto max-h-24">
            {events.map((event, idx) => (
              <button
                key={`${event.fair_id}-${idx}`}
                onClick={() => handleEventClick(event)}
                className={`w-full text-left text-xs p-1 rounded border transition-all hover:shadow-md ${getAvailabilityColor(event)}`}
                title={`${event.name} - ${event.city}, ${event.state}`}
              >
                <div className="font-semibold truncate">{event.name}</div>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="text-xs flex items-center gap-0.5">
                    <MapPin className="w-3 h-3" />
                    {event.city}
                  </span>
                  <span className="text-xs font-medium">
                    {getAvailabilityBadge(event)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      );

      // Start a new week after Saturday
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    // Fill remaining cells in last week
    while (currentWeek.length > 0 && currentWeek.length < 7) {
      currentWeek.push(
        <div key={`empty-end-${currentWeek.length}`} className="min-h-32 bg-gray-50 border border-gray-200 p-2" />
      );
    }
    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }

    return (
      <div className="grid grid-cols-7 gap-0">
        {/* Day headers */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="bg-gray-100 border border-gray-300 p-2 text-center font-semibold text-gray-700">
            {day}
          </div>
        ))}

        {/* Calendar grid */}
        {weeks.map((week, weekIdx) => (
          <React.Fragment key={weekIdx}>
            {week}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-8 h-8 text-secondary" />
            Fair Calendar
          </h1>
          <button
            onClick={goToToday}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
          >
            Today
          </button>
        </div>

        {/* Month navigation */}
        <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <button
            onClick={previousMonth}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-6 h-6 text-gray-700" />
          </button>

          <h2 className="text-2xl font-semibold text-gray-800">
            {monthNames[month - 1]} {year}
          </h2>

          <button
            onClick={nextMonth}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Next month"
          >
            <ChevronRight className="w-6 h-6 text-gray-700" />
          </button>
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-100 border border-green-400 rounded"></div>
            <span className="text-gray-700">Good Availability (&gt;50%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-100 border border-yellow-400 rounded"></div>
            <span className="text-gray-700">Limited (10-50%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-100 border border-red-400 rounded"></div>
            <span className="text-gray-700">Very Limited (&lt;10%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-200 border border-gray-400 rounded"></div>
            <span className="text-gray-700">Sold Out (Waitlist Available)</span>
          </div>
        </div>

        {/* Stats */}
        {calendarData && (
          <div className="mt-4 text-sm text-gray-600">
            Showing {calendarData.total_events} event{calendarData.total_events !== 1 ? 's' : ''} this month
          </div>
        )}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading calendar...</p>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-800">{error}</p>
          <button
            onClick={loadCalendarData}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Calendar grid */}
      {!loading && !error && calendarData && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {renderCalendar()}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && calendarData && calendarData.total_events === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg mt-4">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">No events scheduled for {monthNames[month - 1]} {year}</p>
          <p className="text-gray-500 mt-2">Try browsing other months or check back later</p>
        </div>
      )}
    </div>
  );
};

export default FairCalendar;
